// Fetch wrapper for the backend API. Deliberately has no React import: the
// access token and the "what to do on 401" behavior are injected from
// AuthContext via configureApiClient(), so this module stays a plain,
// framework-agnostic utility.

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const API_PREFIX = '/api/v1';

let getAccessToken = () => null;
let onUnauthorized = async () => false;
let getTenantSlug = () => null;

/**
 * Wires this client up to the running AuthContext/TenantContext instances.
 * Each provider calls this independently with just the handler(s) it owns,
 * so calls don't clobber each other.
 * @param {{ getAccessToken?: () => string | null, onUnauthorized?: () => Promise<boolean>, getTenantSlug?: () => string | null }} handlers
 */
export function configureApiClient(handlers) {
  if (handlers?.getAccessToken) getAccessToken = handlers.getAccessToken;
  if (handlers?.onUnauthorized) onUnauthorized = handlers.onUnauthorized;
  if (handlers?.getTenantSlug) getTenantSlug = handlers.getTenantSlug;
}

export class ApiError extends Error {
  constructor({ code, message, details, status }) {
    super(message || 'Request failed');
    this.name = 'ApiError';
    this.code = code || 'UNKNOWN_ERROR';
    this.details = details;
    this.status = status;
  }
}

async function parseEnvelope(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }

  if (!response.ok) {
    const error = body?.error || {};
    throw new ApiError({
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || response.statusText || 'Request failed',
      details: error.details,
      status: response.status,
    });
  }

  return body || { data: null };
}

// Builds headers, fetches, and handles the 401-refresh-retry-once dance --
// returns the raw Response. Split out from request() so getBlob() (used
// for authenticated binary assets, e.g. document thumbnails/downloads --
// the access token lives in memory only, so a plain <img src> can't
// attach it) can reuse all of this without going through JSON envelope
// parsing.
async function fetchWithAuth(path, options = {}, isRetry = false) {
  const headers = new Headers(options.headers || {});
  const hasBody = options.body !== undefined && options.body !== null;
  if (hasBody && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // The backend resolves the tenant from the subdomain in production; in
  // local dev (plain "localhost", no subdomain) it falls back to this
  // header for every route that doesn't carry tenantSlug in its own JSON
  // body (login/password-reset-request are the only ones that do).
  if (!headers.has('X-Tenant-Slug')) {
    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      headers.set('X-Tenant-Slug', tenantSlug);
    }
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${API_PREFIX}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (networkError) {
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: networkError?.message || 'Network request failed',
      status: 0,
    });
  }

  const isRefreshCall = path.startsWith('/auth/refresh');
  if (response.status === 401 && !isRetry && !isRefreshCall) {
    const recovered = await onUnauthorized();
    if (recovered) {
      return fetchWithAuth(path, options, true);
    }
  }

  return response;
}

async function request(path, options = {}) {
  return parseEnvelope(await fetchWithAuth(path, options));
}

async function requestBlob(path, options = {}) {
  const response = await fetchWithAuth(path, options);
  if (!response.ok) {
    let body = null;
    try {
      body = await response.json();
    } catch (e) {
      body = null;
    }
    const error = body?.error || {};
    throw new ApiError({
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || response.statusText || 'Request failed',
      details: error.details,
      status: response.status,
    });
  }
  return response.blob();
}

function toQueryString(params) {
  if (!params) return '';
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const apiClient = {
  get(path, params) {
    return request(`${path}${toQueryString(params)}`, { method: 'GET' });
  },
  post(path, body) {
    return request(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  patch(path, body) {
    return request(path, { method: 'PATCH', body: JSON.stringify(body) });
  },
  delete(path) {
    return request(path, { method: 'DELETE' });
  },
  // For multipart file uploads -- pass a FormData body directly.
  // request() already skips setting a Content-Type header for FormData
  // (the browser sets its own, including the multipart boundary).
  upload(path, formData) {
    return request(path, { method: 'POST', body: formData });
  },
  // For authenticated binary assets. Returns a Blob instead of a parsed
  // JSON envelope -- pair with URL.createObjectURL() and revoke it on
  // unmount (see shared/components/AuthImage.jsx).
  getBlob(path, params) {
    return requestBlob(`${path}${toQueryString(params)}`, { method: 'GET' });
  },
};
